import React, { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

import moment from "moment";

import { getCustomerSettings } from "@/api/settings";

import { CalendarIcon24, CardArrowIcon48, TimeIcon24, UsersIcon24 } from "@/components/icons";

import type { UserInfo } from "@/types/sessions";

import { Client } from "@/enums/clients";

import useMediaQuery from "@/utils/hooks/use-media-query";

import { getTimeDifference } from "../mini-sessions-list/utils";

import type { SessionCardProps } from "./session-card.types";

import {
    ArrowWrapper,
    LogoWrapper,
    InfoWrapper,
    SessionCardWrapperDiv,
    StyledImage,
    UserInfoWrapper,
    DateTimeAndDurationWrapper,
    DateWrapper,
    TimeWrapper,
    DurationWrapper,
    ContentWrapper,
    TitleWrapper,
    SecondaryWrapperDiv,
    TruncatedStyledText,
    SimulationName,
    ScenarioName,
    ScenarioAndBulletPointWrapper,
    BulletPoint,
    DateText,
    DurationText,
    TimeText,
    MultipleUsersDiv,
    PlusDiv,
    PlusUsersList,
    PlusWrapper,
    PlusUser,
} from "./session-card.styles";

const SessionCard = ({ sessionInfo, scenarioInfo, index }: SessionCardProps) => {
    const navigate = useNavigate();

    const [isPlusDivHovered, setIsPlusDivHovered] = useState<boolean>(false);

    const isDesktopLargeMedia = useMediaQuery("(min-width: 1920px)");

    const { id, traineeName, simulationName, logoUrl, userId, email } = sessionInfo;

    const { simulationId, scenarioName, scenarioInstanceId, from, to } = scenarioInfo;

    let sanitizedSimulationName = simulationName.replace(/_/g, " ") ?? "";
    let sanitizedScenarioName = scenarioName;

    // Use '|' in dashboard names to prevent simulation name concatenation
    if (sanitizedSimulationName.startsWith("|")) {
        sanitizedSimulationName = sanitizedSimulationName.substring(1);
        sanitizedScenarioName = "";
    }

    const duration = (from: any, to: any) => {
        return getTimeDifference(new Date(from), new Date(to));
    };

    const traineeNameInParts = traineeName.split(" ");

    const noNameProvided = traineeNameInParts.every((name: string) => {
        return name === "" || name.includes("undefined");
    });

    const traineeFullName =
        traineeNameInParts[0] + " " + traineeNameInParts[traineeNameInParts.length - 2];

    const traineeNameToDisplay = () => {
        if (noNameProvided) {
            return email ?? "";
        }

        if (traineeFullName.includes("undefined")) {
            return "";
        } else {
            return traineeFullName;
        }
    };

    const plusNumberOfRemainingUsers = traineeNameInParts[traineeNameInParts.length - 1];

    const formattedDateAndTime = moment(scenarioInfo.from).format("DD/MM/YYYY, HH:mm");
    const [date, time] = formattedDateAndTime.split(",");

    let simulation = sanitizedSimulationName ? sanitizedSimulationName : "";
    let scenario = sanitizedScenarioName ? sanitizedScenarioName : "";

    const options = {
        suspense: false,
        refetchOnMount: false,
        useErrorBoundary: true,
    };

    const { data: d } = useQuery([getCustomerSettings], getCustomerSettings, options);

    if (d?.customerName == Client.MLRS) {
        simulation = "TCT - MLRS";
        scenario = "Detachment Commanders Course";
    }

    const cardLink = `/sessions/session-analysis?traineeName=${traineeName}&simulationName=${simulationName}&userId=${userId}&from=${from}&to=${to}&scenarioId=${simulationId}&simulationInstanceId=${id}&scenarioInstanceId=${scenarioInstanceId}&name=${scenarioName}&logoUrl=${logoUrl}`;
    const handleCardClick = (event: any) => {
        event.preventDefault();
        navigate(cardLink);

        if (sessionInfo.users.length != 0) {
            localStorage.setItem("users", JSON.stringify(sessionInfo.users));
        }
    };

    const handleMouseOver = useCallback(() => {
        setIsPlusDivHovered(true);
    }, []);

    const handleMouseOut = useCallback(() => {
        setIsPlusDivHovered(false);
    }, []);

    return (
        <SessionCardWrapperDiv
            onClick={(event: React.MouseEvent) => handleCardClick(event)}
            href={cardLink}
            data-test-id={`session-${index + 1}`}
        >
            <SecondaryWrapperDiv>
                <ContentWrapper>
                    <LogoWrapper>
                        <StyledImage width={isDesktopLargeMedia ? 64 : 48} src={logoUrl} />
                    </LogoWrapper>

                    <InfoWrapper>
                        <TitleWrapper>
                            <SimulationName>{simulation}</SimulationName>
                            <ScenarioAndBulletPointWrapper>
                                <BulletPoint>{"\u2022"}</BulletPoint>
                                <ScenarioName>{scenario}</ScenarioName>
                            </ScenarioAndBulletPointWrapper>
                        </TitleWrapper>

                        <UserInfoWrapper>
                            {!noNameProvided && <UsersIcon24 fill="#999999" />}
                            {traineeName.includes("+") ? (
                                <MultipleUsersDiv>
                                    <TruncatedStyledText>
                                        {traineeNameToDisplay()}
                                    </TruncatedStyledText>

                                    <PlusWrapper
                                        onMouseOver={handleMouseOver}
                                        onMouseOut={handleMouseOut}
                                    >
                                        <PlusDiv>{plusNumberOfRemainingUsers}</PlusDiv>

                                        {isPlusDivHovered && (
                                            <PlusUsersList
                                                onClick={(e: React.MouseEvent) => {
                                                    e.preventDefault();
                                                    e.stopPropagation(); //in order for the list not to be clickable
                                                }}
                                            >
                                                {sessionInfo?.users
                                                    .slice(1)
                                                    ?.map((user: UserInfo) => {
                                                        return (
                                                            <PlusUser key={user.id}>
                                                                <UsersIcon24
                                                                    width={18}
                                                                    height={18}
                                                                />
                                                                {user.name.length === 0
                                                                    ? user.email
                                                                    : user.name}
                                                            </PlusUser>
                                                        );
                                                    })}
                                            </PlusUsersList>
                                        )}
                                    </PlusWrapper>
                                </MultipleUsersDiv>
                            ) : (
                                <TruncatedStyledText>{traineeNameToDisplay()}</TruncatedStyledText>
                            )}
                        </UserInfoWrapper>

                        <DateTimeAndDurationWrapper>
                            <DateWrapper>
                                <CalendarIcon24 fill="#999999" />
                                <DateText>{date}</DateText>
                            </DateWrapper>

                            <TimeWrapper>
                                <TimeIcon24 fill="#999999" />
                                <TimeText>{time}</TimeText>
                            </TimeWrapper>
                            {isDesktopLargeMedia && (
                                <DurationWrapper>
                                    <DurationText>{"\u2022"}</DurationText>
                                    <DurationText>{duration(from, to)}</DurationText>
                                </DurationWrapper>
                            )}
                        </DateTimeAndDurationWrapper>
                    </InfoWrapper>
                </ContentWrapper>

                <ArrowWrapper>
                    <CardArrowIcon48 />
                </ArrowWrapper>
            </SecondaryWrapperDiv>
        </SessionCardWrapperDiv>
    );
};

export default SessionCard;
